import { Box, Card, FormControl, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface SingleSelectInfo {
  options: SingleSelectOption[];
  header: string;
  description: string;
  styleInfo: SingleSelectStyleInfo;
  selectionChangeHandler: (damageCode: string) => void;
}

export interface SingleSelectOption {
  isSelected: boolean;
  text: string;
  id: string;
  mask?: boolean;
}

export interface SingleSelectStyleInfo {
  headerStyle: {
    fontFamily: string;
    fontWeight: number;
    fontSize: number;
    color: string;
    lineHeight: string;
  };
  descriptionStyle: {
    fontFamily: string;
    fontWeight: number;
    fontSize: number;
    color: string;
    lineHeight: string;
  };
  optionStyle: {
    fontFamily: string;
    fontWeight: number;
    fontSize: number;
    color: string;
    lineHeight: string;
    radioButtonColor: string;
    radioButtonSelectedColor: string;
    radioButtonSize?: string;
    borderColor?: string;
    selectedBorderColor?: string;
    borderRadius?: string;
    optionHeight?: string;
  };
}

export default function SingleSelect(props: SingleSelectInfo) {
  /*High level logic*/
  /*
  Two states are maintained.
  1) 'selectedOptionId' to track the id of selection. No default value during init. 
  Any change to this state will trigger a handler in parent via 'useEffect' hook.
  UI interaction can trigger the state change. If there is a value that was selected earlier, 
  function 'syncSelectedIdsInfo' is called once for state sync during initial page load, which essentially 
  updates 'selectedOptionId' state.
  2) 'options' to maintain states that is used for UI rendering. 
  Default value for init will be passed via props. 
  UI interaction can trigger the state change. 
  A selection event will update both 'selectedOptionId' and 'options' states.
  */
  const [selectedOptionId, setSelectedOptionId] = useState('');
  const [options, setOptions] = useState<SingleSelectOption[]>(props.options);
  const [wrappedOptions, setWrappedOptions] = useState<Set<number>>(new Set());
  const textRefs = useRef<(HTMLElement | null)[]>([]);

  const updateLocalStateOnSelectedOptionId = useCallback(
    (selectedId: string) => {
      if (selectedOptionId !== selectedId) {
        setSelectedOptionId(selectedId);
      }
    },
    [selectedOptionId]
  );

  // This is executed only once after initial rendering
  useEffect(() => {
    setOptions(props.options);
    function syncSelectedIdsInfo(options: SingleSelectOption[]) {
      options.forEach((option) => {
        if (option.isSelected) {
          updateLocalStateOnSelectedOptionId(option.id);
        }
      });
    }
    syncSelectedIdsInfo(props.options);
  }, [props.options, updateLocalStateOnSelectedOptionId]);

  // Update parent function
  const { selectionChangeHandler } = props;
  const updateParentOnSelectedOption = useCallback(() => {
    selectionChangeHandler(selectedOptionId);
  }, [selectedOptionId, selectionChangeHandler]);

  // This is be executed when the state changes
  useEffect(() => {
    if (selectedOptionId !== '') {
      updateParentOnSelectedOption();
    }
  }, [selectedOptionId, updateParentOnSelectedOption]);

  // Detect text wrapping
  useEffect(() => {
    const checkWrapping = () => {
      const wrapped = new Set<number>();
      textRefs.current.forEach((ref, index) => {
        if (ref) {
          // Check if the element height exceeds a single line (accounting for line-height)
          const lineHeight = parseFloat(getComputedStyle(ref).lineHeight);
          const height = ref.clientHeight;
          if (height > lineHeight * 1.5) {
            wrapped.add(index);
          }
        }
      });
      setWrappedOptions(wrapped);
    };

    checkWrapping();
    window.addEventListener('resize', checkWrapping);
    return () => window.removeEventListener('resize', checkWrapping);
  }, [options]);

  // Update the parent on selected option id
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Update local state with selected option Id
    const selectedId = (event.target as HTMLInputElement).value;
    if (selectedId !== '') {
      setSelectedOptionId(selectedId);
    }
    // Update the local state of 'options' to reflect the 'selected' state of radio button
    updateLocalStateOnOptions(selectedId);
  };

  function updateLocalStateOnOptions(selectedId: string) {
    if (selectedId !== selectedOptionId) {
      const updatedOptions = options.map((option) => {
        if (option.id === selectedId) {
          option.isSelected = true;
        }
        if (option.id !== selectedId) {
          option.isSelected = false;
        }
        return option;
      });
      setOptions(updatedOptions);
    }
  }

  const displayOption = (option: SingleSelectOption) => {
    if (option.mask) {
      return (
        <span data-dd-privacy="mask" data-dd-action-name="Option">
          {option.text}
        </span>
      );
    } else {
      return option.text;
    }
  };

  const singleSelect = (
    <FormControl sx={{ width: '100%' }}>
      <Typography gutterBottom sx={{ ...props.styleInfo.headerStyle, marginBottom: '1.5rem' }}>
        {props.header}
      </Typography>
      {props.description && (
        <Typography sx={{ ...props.styleInfo.descriptionStyle, marginBottom: '1.5rem' }} gutterBottom>
          {props.description}
        </Typography>
      )}

      <RadioGroup name="single-select-options-group" onChange={handleChange} sx={{ width: '100%' }}>
        {options &&
          options.length > 0 &&
          options.map((option, index) => (
            <Card
              sx={{
                marginTop: '.5rem',
                mx: '4px',
                px: '15px',
                py: wrappedOptions.has(index) ? '.5rem' : 0,
                border:
                  option.id === selectedOptionId
                    ? props.styleInfo.optionStyle.selectedBorderColor
                    : props.styleInfo.optionStyle.borderColor,
                borderRadius: props.styleInfo.optionStyle.borderRadius,
                boxShadow: 'none',
                minHeight: props.styleInfo.optionStyle.optionHeight,
                justifyContent: wrappedOptions.has(index) ? 'flex-start' : 'center',
                display: 'flex',
              }}
              key={option.id}
            >
              <FormControlLabel
                value={option.id}
                control={
                  <Radio
                    disableRipple
                    sx={{
                      paddingTop: 0,
                      paddingBottom: 0,
                      marginTop: wrappedOptions.has(index) ? '0.15rem' : 0,
                      '& svg': {
                        width: props.styleInfo.optionStyle.radioButtonSize,
                        height: props.styleInfo.optionStyle.radioButtonSize,
                      },
                      color: props.styleInfo.optionStyle.radioButtonColor,
                      '&.Mui-checked': {
                        color: props.styleInfo.optionStyle.radioButtonSelectedColor,
                      },
                    }}
                  />
                }
                label={
                  <Typography ref={(el) => (textRefs.current[index] = el)} sx={{ ...props.styleInfo.optionStyle }}>
                    {displayOption(option)}
                  </Typography>
                }
                checked={option.isSelected === true}
                key={option.id}
                tabIndex={index + 1}
                sx={{ width: '100%', alignItems: wrappedOptions.has(index) ? 'flex-start' : 'center' }}
              />
            </Card>
          ))}
      </RadioGroup>
    </FormControl>
  );
  return <Box sx={{ width: '100%' }}>{singleSelect}</Box>;
}
